import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Briefcase, Calendar, MapPin, User, Eye } from "lucide-react";
import { getJobDetails } from "../Services/CAllJobsService";
import JobDescriptionModal from "../../../Employee/Jobs/DraftJob/Modals/JobDescriptionModal";
import NormalAlert from "../../../Alerts/NormalAlert";

const ViewJobDetails = () => {
  const { jobUID } = useParams();
  const navigate = useNavigate();

  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [showJDModal, setShowJDModal] = useState(false);

  useEffect(() => {
    fetchJobDetails();
  }, [jobUID]);

  const fetchJobDetails = async () => {
    try {
      const res = await getJobDetails(jobUID);
      setJobDetails(res.data.jobData[0]);
    } catch (error) {
      setAlert({
        type: "fail",
        message:
          error?.response?.data?.message || "Unable to fetch job details.",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatIndianCurrency = (amount) =>
    new Intl.NumberFormat("en-IN").format(amount);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    try {
      return dateStr.split("T")[0];
    } catch {
      return "";
    }
  };

  if (loading)
    return (
      <div className="text-center mt-20 text-gray-500">
        Loading job details...
      </div>
    );

  if (!jobDetails)
    return (
      <div className="text-center mt-20 text-red-500">
        No job details found.
      </div>
    );

  const InfoCard = ({ label, value }) => (
    <div className="bg-white rounded-xl p-3 sm:p-4 md:p-5 border border-gray-100 shadow-sm hover:shadow-md transition">
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">
        {label}
      </p>
      <p className="font-semibold text-gray-800 text-sm">{value || "-"}</p>
    </div>
  );

  const SectionHeader = ({ icon: Icon, title }) => (
    <div className="flex items-center gap-3 mb-4 border-b pb-2">
      <div className="p-2 bg-blue-50 rounded-lg">
        <Icon size={18} className="text-blue-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-gray-100 px-3 sm:px-5 md:px-8 py-6 md:py-10 mt-[55px]">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* HEADER */}
          <div className="bg-master-header rounded-xl shadow-sm p-6 border">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold break-words text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] md:drop-shadow-none">
                  {jobDetails.jobTitle} ({jobDetails.roleCode})
                </h1>

                <div className="flex gap-3 mt-3 flex-wrap">
                  <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-700">
                    {jobDetails.positionType}
                  </span>

                  <span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-700">
                    {jobDetails.numberOfOpenings} Openings
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setShowJDModal(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm sm:text-base bg-[linear-gradient(60deg,#0D47A1,#3381cf)] rounded-lg text-white"
                >
                  <Eye size={16} />
                  View Job Description
                </button>

                <Link
                  to={`/Consultant/Dashboard/Send-Candidate/${jobUID}`}
                  className="px-4 py-2 text-sm sm:text-base text-center bg-[linear-gradient(60deg,#0D47A1,#3381cf)] text-white rounded-lg"
                >
                  Submit Candidate
                </Link>
              </div>
            </div>
          </div>

          {/* ROLE + MANAGER */}
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-sm p-8 border">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Briefcase className="text-blue-500" size={20} />
                  <h2 className="text-lg font-semibold text-gray-800">
                    Role Information
                  </h2>
                </div>

                {/* Badge */}
                <span
                  className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold ${
                    jobDetails.roleCategory === "on-roll"
                      ? "bg-green-100 text-green-700 border border-green-300"
                      : "bg-orange-100 text-orange-700 border border-orange-300"
                  }`}
                >
                  {jobDetails.roleCategory}
                </span>
              </div>

              <div className="border-b border-gray-200 mb-6"></div>
              {/* Content */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <InfoCard label="Function" value={jobDetails.function} />
                <InfoCard label="Department" value={jobDetails.department} />
                <InfoCard label="Grade" value={jobDetails.grade} />
                <InfoCard label="Job Category" value={jobDetails.jobCategory} />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-8 border">
              <SectionHeader icon={MapPin} title="Location" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <InfoCard label="Location" value={jobDetails.location} />
                <InfoCard label="Zone" value={jobDetails.zone} />
              </div>
            </div>
          </div>

          {/* LOCATION + COMPENSATION */}
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-sm p-8 border">
              <SectionHeader
                icon={Calendar}
                title="Compensation & Experience"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <InfoCard
                  label="Budget"
                  value={formatIndianCurrency(jobDetails.offeredSalary)}
                />

                <InfoCard
                  label="Experience (in years)"
                  value={`${jobDetails.minimumExperience} - ${jobDetails.maximumExperience}`}
                />

                <InfoCard
                  label="Qualification"
                  value={jobDetails.minimumQualification}
                />

                <InfoCard
                  label="Gender Preference"
                  value={jobDetails.genderPreference}
                />

                <InfoCard
                  label="Requisition Date"
                  value={formatDate(jobDetails.requisitionDate)}
                />
              </div>
            </div>
          </div>

          {/* BACK BUTTON */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate("/Consultant/Dashboard/All-Jobs")}
              className="px-4 sm:px-6 py-2 text-sm sm:text-base border border-gray-400 text-gray-600 rounded-full hover:bg-gray-200 transition"
            >
              ← Back to Jobs
            </button>
          </div>
        </div>
      </div>

      {/* JD MODAL */}
      <JobDescriptionModal
        show={showJDModal}
        onHide={() => setShowJDModal(false)}
        fullPath={jobDetails.jobDescription}
      />

      {/* ALERT */}
      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default ViewJobDetails;
